import { useState, useEffect } from 'react';
import { fetchJourney, fetchJourneyReport } from '../services/journeyApi';

// Both endpoints answer with the same outer shape — period → ai → the main
// visual → indicators — so one hook serves all five tabs and the page reads a
// single loading / error surface.
const EMPTY_AI = { summary: [], summary_fr: [], insights: [], insights_fr: [] };

const messageForError = (err) => {
	if (err?.status === 422) {
		return 'That range is too long to report on. Pick a window of a year or less.';
	}
	return 'We could not load your journey just now. Please try again.';
};

/**
 * @param {object} params
 * @param {'day'|'week'|'month'|'year'|'report'} params.view
 * @param {string} params.date - Anchor date for the calendar views
 * @param {number|string|null} params.goalSettingId - Calendar cells only
 * @param {string} params.reportPeriod
 * @param {string} params.reportStart - custom range only
 * @param {string} params.reportEnd - custom range only
 * @param {string} params.userId
 */
const useJourney = ({
	view,
	date,
	goalSettingId,
	reportPeriod,
	reportStart,
	reportEnd,
	userId,
}) => {
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const isReport = view === 'report';
	// A custom range is only requestable once both ends are filled in.
	const customIncomplete =
		isReport && reportPeriod === 'custom' && !(reportStart && reportEnd);

	useEffect(() => {
		if (!userId || customIncomplete) {
			setLoading(false);
			return undefined;
		}

		let isCancelled = false;
		setLoading(true);
		setError(null);
		// The previous response deliberately stays in state: swapping it for a
		// loading message on every arrow press made the page blink.

		const request = isReport
			? fetchJourneyReport(
					{ period: reportPeriod, start: reportStart, end: reportEnd },
					userId
			  )
			: fetchJourney({ view, date, goalSettingId }, userId);

		request
			.then((response) => {
				if (isCancelled) return;
				setData(response);
			})
			.catch((err) => {
				if (isCancelled) return;
				console.error('Failed to load journey:', err);
				setData(null);
				setError(messageForError(err));
			})
			.finally(() => {
				if (!isCancelled) setLoading(false);
			});

		return () => {
			isCancelled = true;
		};
	}, [
		view,
		date,
		goalSettingId,
		isReport,
		reportPeriod,
		reportStart,
		reportEnd,
		customIncomplete,
		userId,
	]);

	// Held-over data is only safe to show while the shape still matches the tab
	// the user is looking at: stepping to the next week keeps its grid on screen,
	// but switching week -> day must not render last week's cells as a timeline.
	const matchesView = isReport
		? Boolean(data?.period_type)
		: data?.view === view;
	const usable = matchesView ? data : null;

	return {
		data: usable,
		period: usable?.period ?? null,
		ai: usable?.ai ?? EMPTY_AI,
		cells: usable?.cells ?? [],
		entries: usable?.entries ?? [],
		indicators: usable?.indicators ?? [],
		// Nothing to show yet: the placeholder is the only thing available.
		loading: loading && !usable,
		// Something is on screen and still current enough to leave up.
		refreshing: loading && Boolean(usable),
		error,
		// True while the user has picked a custom range but not finished it.
		awaitingCustomRange: customIncomplete,
	};
};

export default useJourney;
